import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { UnlockRequestResponse } from '@k-management/shared';
import { Link } from 'react-router';
import { usePageTitle } from '../../components/layout/page-chrome.js';
import { apiRequest } from '../../lib/api.js';
import { formatTimestamp } from '../../lib/format.js';
import { registryKeys } from './registry-ui.js';

/**
 * Subjects already in the archive that a user asked to see (proposal §2.6):
 * the administrator unlocks them for that user, or refuses.
 */
export function UnlockRequestsPage() {
  usePageTitle('Richieste di sblocco');
  const queryClient = useQueryClient();
  const requests = useQuery({
    queryKey: registryKeys.requests,
    queryFn: () => apiRequest<UnlockRequestResponse[]>('/unlock-requests'),
  });
  const decide = useMutation({
    mutationFn: ({ id, approve }: { id: string; approve: boolean }) =>
      apiRequest<UnlockRequestResponse[]>(`/unlock-requests/${id}/decision`, {
        method: 'POST',
        body: { approve },
      }),
    onSuccess: async (updated) => {
      queryClient.setQueryData(registryKeys.requests, updated);
      await queryClient.invalidateQueries({ queryKey: registryKeys.all });
      await queryClient.invalidateQueries({ queryKey: [...registryKeys.requests, 'count'] });
    },
  });

  return (
    <section className="stack">
      <div className="page-header">
        <div>
          <h1>Richieste di sblocco</h1>
          <p className="muted">
            Anagrafiche già in archivio che un utente ha provato a inserire. Finché non le sblocchi,
            l’utente non ne vede né il nome né i dati.
          </p>
        </div>
      </div>
      {requests.isPending && <p className="muted">Caricamento…</p>}
      {requests.isError && <p className="error">{requests.error.message}</p>}
      {requests.data && (
        <div className="table-wrap card flush">
          <table className="data-table">
            <thead>
              <tr>
                <th>Quando</th>
                <th>Chi chiede</th>
                <th>Identificativo inserito</th>
                <th>Soggetto in archivio</th>
                <th>In carico a</th>
                <th>Stato</th>
                <th aria-label="Azioni" />
              </tr>
            </thead>
            <tbody>
              {requests.data.map((request) => (
                <tr key={request.id}>
                  <td>
                    {formatTimestamp(request.createdAt)}
                    {request.message && <small className="muted block">«{request.message}»</small>}
                  </td>
                  <td>
                    <strong>{request.requestedBy.name}</strong>
                    <small className="muted block">{request.requestedBy.email}</small>
                  </td>
                  <td className="code">{request.taxId}</td>
                  <td>
                    <Link to={`/anagrafica/${request.subject.id}`}>{request.subject.name}</Link>
                  </td>
                  <td>{request.subject.owner ?? '—'}</td>
                  <td>
                    {request.status === 'pending' ? (
                      <span className="pill warn">In attesa</span>
                    ) : request.status === 'approved' ? (
                      <span className="pill ok">Sbloccata</span>
                    ) : (
                      <span className="pill muted">Rifiutata</span>
                    )}
                    {request.decidedBy && (
                      <small className="muted block">
                        {request.decidedBy}
                        {request.decidedAt && `, ${formatTimestamp(request.decidedAt)}`}
                      </small>
                    )}
                  </td>
                  <td className="grid-actions">
                    {request.status === 'pending' && (
                      <>
                        <button
                          type="button"
                          className="small primary"
                          disabled={decide.isPending}
                          onClick={() => decide.mutate({ id: request.id, approve: true })}
                        >
                          Sblocca
                        </button>{' '}
                        <button
                          type="button"
                          className="small"
                          disabled={decide.isPending}
                          onClick={() => decide.mutate({ id: request.id, approve: false })}
                        >
                          Rifiuta
                        </button>
                      </>
                    )}
                  </td>
                </tr>
              ))}
              {requests.data.length === 0 && (
                <tr>
                  <td colSpan={7} className="grid-empty">
                    Nessuna richiesta.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}
      {decide.error && <p className="error">{decide.error.message}</p>}
    </section>
  );
}
